import * as React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Grid, Paper, Button, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import NoRecords from '../Shared/NoRecords';
import { useConsentStyles } from '../Authorize/styles';
import GrantItem from './grant-item';

// Same page card as Profile/config.
const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: '10%',

    [theme.breakpoints.up('sm')]: {
      marginTop: '2%',
    },
  },
  paper: {
    padding: '10px',
    paddingBottom: '20px',
    marginBottom: '10px',
  },
  title: { marginBottom: 12, '&:focus': { outline: 'none' } },
  intro: { marginBottom: theme.spacing(2) },
  loading: { textAlign: 'center', padding: 24 },
  retry: { marginTop: 8 },
}));

// The connected apps (MCP grants) of the user, with the way to revoke each.
// `status` is 'loading', 'error' or 'ready'.
export default function ConnectedApps({
  status,
  grants,
  brandName,
  revokingId,
  onRetry,
  onRevoke,
  titleRef,
  buttonRefFor,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const consent = useConsentStyles();

  let content = null;

  if (status === 'loading') {
    content = (
      <div role="status" aria-busy="true" className={classes.loading}>
        <CircularProgress size={32} aria-hidden="true" />
      </div>
    );
  } else if (status === 'error') {
    content = (
      <Alert
        severity="error"
        className={consent.alert}
        action={
          <Button
            type="button"
            color="inherit"
            size="small"
            className={consent.btn}
            onClick={onRetry}
          >
            {t('mcp.consent.tryAgain')}
          </Button>
        }
      >
        {t('mcp.connectedApps.loadError')}
      </Alert>
    );
  } else if (grants.length === 0) {
    content = <NoRecords message={t('mcp.connectedApps.empty')} />;
  } else {
    content = (
      <ul className="list-group list-group-flush">
        {grants.map((grant, index) => (
          <GrantItem
            key={grant.id}
            grant={grant}
            index={index}
            disabled={revokingId !== null}
            onRevoke={onRevoke}
            buttonRef={buttonRefFor(grant.id)}
          />
        ))}
      </ul>
    );
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={12} md={10} lg={6} className={classes.root}>
        <Paper elevation={3} className={classes.paper}>
          {/* Back to the account settings, outside the heading so it stays out of its name. */}
          <Link to="/app/account-configuration" className="pull-right" aria-label={t('Account')}>
            <i className="fa fa-share fa-flip-horizontal" aria-hidden="true"></i>
          </Link>
          {/* tabIndex -1: the focus lands here after the last app is revoked. */}
          <Typography
            ref={titleRef}
            tabIndex={-1}
            variant="h6"
            component="h1"
            align="center"
            className={classes.title}
          >
            {t('mcp.connectedApps.title')}
          </Typography>
          <Typography variant="body2" className={classes.intro}>
            {t('mcp.connectedApps.intro', { brand: brandName })}
          </Typography>
          {content}
        </Paper>
      </Grid>
    </Grid>
  );
}
